const pendingSections = new Map();

function schedule(callback, delay = 0) {
  window.setTimeout(() => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(callback, { timeout: 1200 });
    } else {
      callback();
    }
  }, delay);
}

class CbnLazySection extends HTMLElement {
  #loading = false;
  #attempts = 0;

  connectedCallback() {
    if (this.#loading) return;

    if (!('IntersectionObserver' in window)) {
      schedule(() => this.load(), 500);
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      },
      { rootMargin: '1200px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  async load() {
    if (this.#loading) return;
    this.#loading = true;
    this.setAttribute('aria-busy', 'true');

    try {
      const sectionId = this.dataset.sectionId;
      const url = new URL(this.dataset.sectionUrl || window.location.pathname, window.location.origin);
      url.searchParams.set('sections', sectionId);

      let request = pendingSections.get(url.href);
      if (!request) {
        request = fetch(url.href, { credentials: 'same-origin', priority: 'low' }).then((response) => {
          if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
          return response.json();
        });
        pendingSections.set(url.href, request);
      }

      const [payload] = await Promise.all([
        request,
        this.dataset.moduleUrl ? import(this.dataset.moduleUrl) : Promise.resolve(),
      ]);
      const html = payload?.[sectionId];
      if (!html) throw new Error('Section response was empty');

      const template = document.createElement('template');
      template.innerHTML = html.trim();
      const renderedSection = template.content.querySelector(`[id="shopify-section-${sectionId}"]`);
      const currentSection = this.closest('.shopify-section');
      if (!renderedSection || !currentSection) throw new Error('Section wrapper was not found');

      currentSection.innerHTML = renderedSection.innerHTML;
      currentSection.dispatchEvent(new CustomEvent('cbn:section-loaded', { bubbles: true }));
    } catch (error) {
      this.#loading = false;
      this.#attempts += 1;
      this.removeAttribute('aria-busy');

      if (this.#attempts < 3) {
        schedule(() => this.load(), this.#attempts * 1500);
      }
    }
  }
}

if (!customElements.get('cbn-lazy-section')) {
  customElements.define('cbn-lazy-section', CbnLazySection);
}
